import React from 'react';
import { formatAmountWithLocale } from '../helper/common.helper';

interface WizardSummaryProps {
  calculatedSubAmount?: any;      // API calc response (source of truth when present)
  calculatedAmounts?: any;        // live local fallback
  currency?: string;
  freelancers?: any[];            // selectedFreelancersForInvoice
  currentUser?: any;              // auth user (for resolving "createdBy")
  isLoading?: boolean;            // calc in flight
  expressPayout?: boolean;
  language?: string;
  t: (key: string, opts?: any) => string;
  onEdit?: () => void;            // when set, shows an Edit action in the card header
  editLabel?: string;
}

export const WizardSummary: React.FC<WizardSummaryProps> = ({
  calculatedSubAmount,
  calculatedAmounts = {},
  currency = 'SEK',
  freelancers = [],
  currentUser,
  isLoading = false,
  expressPayout = false,
  language = 'en',
  t,
  onEdit,
  editLabel,
}) => {
  const tc = calculatedSubAmount?.response?.totalCalculation;
  const freelancersData: any[] = calculatedSubAmount?.response?.freelancersData || [];
  const curr = tc?.currencyName || currency || 'SEK';

  const fmt = (amount: number) => formatAmountWithLocale(Number(amount) || 0, curr, language);

  // Normalised figures — prefer the API total, fall back to local live amounts
  const subtotal = tc?.amount ?? calculatedAmounts?.net ?? 0;                 // ex VAT
  const vatAmt = tc?.vat ?? calculatedAmounts?.tax ?? 0;
  const vatPct = tc?.vatPercentage;
  const rut = tc?.rutDeductionAmount ?? calculatedAmounts?.rutDeductionAmount ?? 0;
  const rot = tc?.rootDeductionAmount ?? calculatedAmounts?.rootDeductionAmount ?? 0;
  const express = tc?.expressFee ?? calculatedAmounts?.expressFee ?? 0;
  const customerPays = tc?.amountIncludingVatAfterHouseholdServiceDeduction;
  const grand = tc?.amountIncludingVat ?? calculatedAmounts?.amountIncludingVat ?? calculatedAmounts?.amount ?? 0;

  const hasDeduction = (Number(rut) || 0) > 0 || (Number(rot) || 0) > 0;
  const hasData = (Number(grand) || 0) > 0 || (Number(subtotal) || 0) > 0;

  // Resolve a display name for a freelancer's "createdBy" id
  const getName = (createdBy: any): string => {
    const idStr = createdBy?.toString();
    const match = freelancers.find(
      (f: any) => (f.userId ?? f.id)?.toString() === idStr
    );
    if (match) return match.name || match.userName || match.email || (t('FREELANCER') as string);
    const cuId = (currentUser?.userId ?? currentUser?.id)?.toString();
    if (cuId && cuId === idStr) {
      const nm = `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim();
      return nm || currentUser?.email || (t('YOU') as string);
    }
    return t('FREELANCER') as string;
  };

  return (
    <div className="iw-summary">
      <div className="iw-card iw-summary-card">
        <div className="iw-summary-head">
          <span className="iw-summary-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2.5" /><path d="M2 10h20" /><path d="M6 15h4" />
            </svg>
          </span>
          <h3>{t('WIZARD_STEP_SUMMARY_TITLE')}</h3>
          {isLoading && <span className="iw-summary-spinner spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
          {onEdit && (
            <button type="button" className="iw-summary-edit" onClick={onEdit}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {editLabel || t('EDIT')}
            </button>
          )}
        </div>

        {!hasData && !isLoading ? (
          <div className="iw-summary-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 7h6M9 11h6M9 15h4M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"
                stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <p>{t('SUMMARY_EMPTY_HINT')}</p>
          </div>
        ) : (
          <>
            <div className="iw-summary-rows">
              <div className="iw-summary-row">
                <span>{t('SUB_TOTAL')}</span>
                <span>{fmt(subtotal)}</span>
              </div>
              <div className="iw-summary-row">
                <span>{t('VAT')}{vatPct !== undefined ? ` (${vatPct}%)` : ''}</span>
                <span>{fmt(vatAmt)}</span>
              </div>
              {(Number(rut) || 0) > 0 && (
                <div className="iw-summary-row is-deduction">
                  <span>{t('RUT_DEDUCTION_AMOUNT')}</span>
                  <span>-{fmt(rut)}</span>
                </div>
              )}
              {(Number(rot) || 0) > 0 && (
                <div className="iw-summary-row is-deduction">
                  <span>{t('ROT_DEDUCTION_AMOUNT')}</span>
                  <span>-{fmt(rot)}</span>
                </div>
              )}
              {expressPayout && (Number(express) || 0) > 0 && (
                <div className="iw-summary-row">
                  <span>{t('EXPRESS_FEE')}</span>
                  <span>{fmt(express)}</span>
                </div>
              )}
              {hasDeduction && customerPays !== undefined && (
                <div className="iw-summary-row is-muted">
                  <span>{t('CUSTOMER_WILL_PAY')}</span>
                  <span>{fmt(customerPays)}</span>
                </div>
              )}
            </div>

            <div className="iw-summary-total">
              <span>{t('GRAND_TOTAL')}</span>
              <span className="iw-summary-total-value">{fmt(grand)}</span>
            </div>
          </>
        )}
      </div>

      {/* Per-freelancer breakdown (collective invoices) */}
      {freelancersData.length > 1 && (
        <div className="iw-card iw-summary-card iw-breakdown">
          <div className="iw-summary-head">
            <h3>{t('PER_FREELANCER_BREAKDOWN')}</h3>
          </div>
          <div className="iw-breakdown-list">
            {freelancersData.map((fd: any, idx: number) => (
              <div className="iw-breakdown-item" key={fd.createdBy ?? idx}>
                <div className="iw-breakdown-name">
                  <span className="iw-breakdown-avatar">
                    {(getName(fd.createdBy) || '?').charAt(0).toUpperCase()}
                  </span>
                  {getName(fd.createdBy)}
                </div>
                <div className="iw-breakdown-amount">
                  {formatAmountWithLocale(Number(fd.amountIncludingVat) || 0, fd.currencyName || curr, language)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WizardSummary;
